import { compareDisplayNames } from '@utils/string.util';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  DeviceSettingEditDialogComponent,
  DeviceSettingViewModel,
  DialogOption,
} from '@components/device-setting-list/device-setting-edit-dialog/device-setting-edit-dialog.component';
import { DatetimePickerComponent } from '@components/shared/datetime-picker/datetime-picker.component';
import { DeviceSettingListViewModel } from '@components/shipment-list/shipment-list.component';
import { DeviceSettingResponse } from '@entities/deviceSetting';
import { DeviceSettingService } from '@services/api/device-setting.service';
import {
  convertToAssetTypeList,
  convertToCommonDeviceSetting,
} from '@utils/device-setting.util';
import { firstValueFrom } from 'rxjs';
import { ShipmentDeviceFormGroup } from '../shipment-edit-dialog.component';

/** デバイスのViewModel */
export type DeviceViewModel = {
  /** シリアルナンバー */
  serialNo: string;
  /** デバイス種類 */
  deviceTypeId: string;
  /** デバイス名 */
  deviceName: string;
};

type DeviceTypeOption = {
  id: string;
  displayName: string;
};

@Component({
  selector: 'app-shipment-device-edit',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormField,
    DatetimePickerComponent,
  ],

  templateUrl: './shipment-device-edit.component.html',
  styleUrl: './shipment-device-edit.component.scss',
})
export class ShipmentDeviceEditComponent implements OnInit, OnChanges {
  @Input() updating: boolean = false;
  @Input({ required: true }) formGroup!: ShipmentDeviceFormGroup;
  @Input()
  deviceSettingList: DeviceSettingListViewModel[] = [];
  @Input() shipmentDeviceSettings: Record<string, DeviceSettingResponse> = {};
  @Input() loadingDeviceSetting = false;

  @Output() deviceSettingUpdated = new EventEmitter<DeviceSettingViewModel>();

  // デバイス種類のリスト
  public deviceTypeList: DeviceTypeOption[] = [];

  // デバイス種類IDで絞り込んだデバイスのリスト
  public deviceListGroupByTypeId: DeviceSettingListViewModel[] = [];

  /** ツールチップメッセージ */
  public tooltipMessage: string;

  /** デバイス設定編集を複数回するとき、2回目以降は前回の変更情報をもとに編集するため、フォームから編集したデバイス設定情報を保持する */
  private deviceSettingBeforeEdit: DialogOption | undefined;

  constructor(
    private deviceSettingService: DeviceSettingService,
    private dialog: MatDialog,
  ) {
    this.tooltipMessage =
      '配送ステータス「出発準備済み」に変更後、何分前より配送センサーアラートを画面に表示し始めるかを指定できます。\n' +
      'なお、本サービスから利用するクラウドサービスの都合上、指定時間から最大5分遅延する可能性があります。';
  }

  ngOnInit(): void {
    this.generateDeviceTypeIdList(this.deviceSettingList);
    setTimeout(() => {
      this.detectFormControlValueChanged();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['formGroup'] !== undefined ||
      changes['loadingDeviceSetting'] !== undefined ||
      changes['deviceSettingList'] !== undefined
    ) {
      // 自分で disable した状態を親フォームの read only と誤認しないよう、
      // 親フォームの disabled 状態と読み込み状態を分けて判定する。
      const parentDisabled = this.formGroup.parent?.disabled ?? false;
      const shouldDisable =
        parentDisabled ||
        changes['loadingDeviceSetting'].currentValue ||
        this.deviceSettingList.length < 1;

      if (shouldDisable) {
        this.formGroup.controls.deviceType.disable({ emitEvent: false });
        this.formGroup.controls.deviceId.disable({ emitEvent: false });
      } else {
        this.formGroup.controls.deviceType.enable({ emitEvent: false });
        this.formGroup.controls.deviceId.enable({ emitEvent: false });
      }
    }

    if (
      (changes['deviceSettingList'] !== undefined ||
        changes['loadingDeviceSetting'] !== undefined) &&
      !this.loadingDeviceSetting
    ) {
      this.generateDeviceTypeIdList(this.deviceSettingList);
      const deviceType = this.formGroup.controls.deviceType.value;
      if (
        deviceType &&
        this.deviceTypeList.some((option) => option.id === deviceType)
      ) {
        this.formGroup.controls.deviceType.setValue(deviceType);
        this.filterDeviceSettingList(deviceType);
        const deviceId = this.formGroup.controls.deviceId.value;
        if (
          deviceId &&
          this.deviceListGroupByTypeId.some(
            (device) => device.deviceId === deviceId,
          )
        ) {
          this.formGroup.controls.deviceId.setValue(deviceId);
        } else {
          this.formGroup.controls.deviceId.setValue(null);
        }
      } else {
        this.formGroup.controls.deviceType.setValue(null);
        this.filterDeviceSettingList(null);
        this.formGroup.controls.deviceId.setValue(null);
      }
    }
  }

  /**
   * 選択されたデバイスの設定を取得する
   * @returns デバイス設定
   */
  public get getSelectedDeviceSetting() {
    const deviceId = this.formGroup.get('deviceId')?.value;
    const deviceSetting = this.deviceSettingList.find(
      (device) => device.deviceId === deviceId,
    );
    return deviceSetting;
  }

  /**
   * デバイス設定詳細ダイアログを表示する
   * @param serialNo シリアル番号
   */
  public async openDeviceSettingDetailEditDialog() {
    const deviceId = this.formGroup.get('deviceId')?.value;
    const device = this.deviceSettingList.find(
      (device) => device.deviceId === deviceId,
    );

    if (!device) return;

    let options!: DialogOption;
    // フォームを開いてから、デバイス設定編集をしていないとき(1回目のデバイス設定編集)
    if (!this.deviceSettingBeforeEdit) {
      options = await this.createDeviceSettingFromApi(device);
    } // 2回目以降のデバイス設定編集のとき
    else {
      options = this.deviceSettingBeforeEdit;
    }

    const editedDeviceSetting: DeviceSettingViewModel = await firstValueFrom(
      this.dialog
        .open(DeviceSettingEditDialogComponent, {
          width: '90%',
          maxWidth: '1500px',
          data: options,
        })
        .afterClosed(),
    );

    if (editedDeviceSetting) {
      editedDeviceSetting.deviceId = options.serialNo;

      // 二回目以降のデバイス設定編集時に前回の編集内容を表示するために、値を保持する
      this.createDeviceSettingBeforeEdit(editedDeviceSetting, options);
      this.deviceSettingUpdated.emit(editedDeviceSetting);
    }
  }

  public selectedDeviceType(): boolean {
    return (
      !this.loadingDeviceSetting && !!this.formGroup.controls.deviceId.value
    );
  }

  private filterDeviceSettingList(deviceType: string | null): void {
    this.deviceListGroupByTypeId = this.deviceSettingList.filter(
      (deviceSetting) =>
        deviceSetting.deviceTypeDisplayName === deviceType,
    );
    // デバイス名の辞書順でソート
    this.deviceListGroupByTypeId.sort((a, b) => {
      return a.deviceName.localeCompare(b.deviceName, undefined, {
        numeric: true,
        ignorePunctuation: true,
      });
    });
  }

  private detectFormControlValueChanged(): void {
    this.formGroup.controls.deviceType.valueChanges.subscribe((deviceType) => {
      if (this.formGroup.enabled && deviceType) {
        this.formGroup.controls.deviceId.enable();
      } else {
        this.formGroup.controls.deviceId.disable();
      }

      this.filterDeviceSettingList(deviceType);
    });
  }

  private generateDeviceTypeIdList(
    deviceSettingList: DeviceSettingListViewModel[],
  ): void {
    const deviceTypeMap = new Map(
      deviceSettingList
        .filter((deviceSetting) => !!deviceSetting.deviceTypeDisplayName)
        .map((deviceSetting) => [
          deviceSetting.deviceTypeDisplayName,
          {
            id: deviceSetting.deviceTypeDisplayName || '',
            displayName: deviceSetting.deviceTypeDisplayName || '',
          },
        ]),
    );
    const deviceTypeList = [...deviceTypeMap.values()];
    deviceTypeList.sort((a, b) => compareDisplayNames(a.displayName, b.displayName));

    this.deviceTypeList = deviceTypeList;
  }

  private async createDeviceSettingFromApi(
    device: DeviceSettingListViewModel,
  ): Promise<DialogOption> {
    const serialNo = device.deviceId;

    const deviceSettingResponse = await firstValueFrom(
      this.deviceSettingService.find(serialNo),
    );

    let commonDeviceSetting = convertToCommonDeviceSetting(
      deviceSettingResponse,
    );
    const shipmentDeviceSettingResponse = this.shipmentDeviceSettings[serialNo];
    if (this.updating && shipmentDeviceSettingResponse) {
      // すでに配送向けにデバイス設定がされていれば、その設定値を用いる
      commonDeviceSetting = convertToCommonDeviceSetting(
        shipmentDeviceSettingResponse,
      );
    }

    const deviceSetting = {
      serialNo: deviceSettingResponse.serialNo,
      deviceName:
        deviceSettingResponse.deviceName || deviceSettingResponse.serialNo,
      status: deviceSettingResponse.status,
      lastUpdatedAt: deviceSettingResponse.lastUpdatedAt,
      plannedUpdatedAt: deviceSettingResponse.plannedUpdatedAt,
      battery: deviceSettingResponse.battery,
      ...commonDeviceSetting,
    };

    const assettypeResponseList = deviceSettingResponse.assettypeList;
    const assettypeList = convertToAssetTypeList(assettypeResponseList);

    let battery = null;
    if (device.battery) {
      battery = Number(device.battery);
    }

    // フォーム全体がdisabledなら、デバイス詳細の編集を無効化する
    // それ以外で、設定グループに所属している場合は無効化する
    let formDisabled = false;
    const targetSettingGroupName = device.deviceSettingGroupName;
    if (this.formGroup.disabled) {
      formDisabled = true;
    } else if (targetSettingGroupName) {
      formDisabled = this.deviceSettingList.some(
        (device) => device.deviceSettingGroupName === targetSettingGroupName,
      );
    }

    const options: DialogOption = {
      updating: true,
      deviceName: deviceSetting.deviceName || deviceSetting.serialNo || '',
      serialNo: deviceSetting.serialNo,
      status: device.status,
      lastUpdatedAt: device.lastCommunicationAt
        ? new Date(device.lastCommunicationAt)
        : null,
      plannedUpdatedAt: new Date(),
      battery: battery,
      assettypeList: assettypeList,
      deviceType: deviceSettingResponse.deviceType,
      isSettingGroupName: formDisabled,
      contractExpiredAt: deviceSettingResponse.deviceContract.contractExpiredAt,
      deviceNameEditDisabled: true,
      isFromShipment: true,
    };

    options.updating = true;
    options.plannedUpdatedAt = device.lastCommunicationAt
      ? new Date(
          new Date(device.lastCommunicationAt).getTime() +
            deviceSetting.reportingFrequency * 60000,
        )
      : null;
    options.target = {
      deviceName: deviceSetting.deviceName || deviceSetting.serialNo,
      samplingFrequency: deviceSetting.samplingFrequency,
      reportingFrequency: deviceSetting.reportingFrequency,
      shipmentTracking: deviceSetting.shipmentTracking,
      locationDetected: deviceSetting.locationDetected,
      locationPriority: [
        deviceSetting.locationPriority?.[0] || '',
        deviceSetting.locationPriority?.[1] || '',
        deviceSetting.locationPriority?.[2] || '',
      ],
      fallDetected: deviceSetting.fallDetected,
      mcdDetected: deviceSetting.mcdDetected,
      lightExposureDetected: deviceSetting.lightExposureDetected,
      lightExposureDetectValue: deviceSetting.lightExposureDetectValue,
      shockDetected: deviceSetting.shockDetected,
      shockDetectedDetectValue: deviceSetting.shockDetectedDetectValue,
      temperature: deviceSetting.temperature,
      temperatureSettings: [
        deviceSetting.temperatureSettings?.[0] || 0,
        deviceSetting.temperatureSettings?.[1] || 0,
        deviceSetting.temperatureSettings?.[2] || 0,
        deviceSetting.temperatureSettings?.[3] || 0,
      ],
      pressure: deviceSetting.pressure,
      pressureSettings: [
        deviceSetting.pressureSettings?.[0] || 0,
        deviceSetting.pressureSettings?.[1] || 0,
        deviceSetting.pressureSettings?.[2] || 0,
        deviceSetting.pressureSettings?.[3] || 0,
      ],
      humidity: deviceSetting.humidity,
      humiditySettings: [
        deviceSetting.humiditySettings?.[0] || 0,
        deviceSetting.humiditySettings?.[1] || 0,
        deviceSetting.humiditySettings?.[2] || 0,
        deviceSetting.humiditySettings?.[3] || 0,
      ],
      tilt: deviceSetting.tilt,
      tiltSettings: [
        deviceSetting.tiltSettings?.[0] || 0,
        deviceSetting.tiltSettings?.[1] || 0,
        deviceSetting.tiltSettings?.[2] || 0,
        deviceSetting.tiltSettings?.[3] || 0,
      ],
      light: deviceSetting.light,
      lightSettings: [
        deviceSetting.lightSettings?.[0] || 0,
        deviceSetting.lightSettings?.[1] || 0,
        deviceSetting.lightSettings?.[2] || 0,
        deviceSetting.lightSettings?.[3] || 0,
      ],
      fmd: deviceSetting.fmd || false,
    };

    return options;
  }

  private createDeviceSettingBeforeEdit(
    editedDeviceSetting: DeviceSettingViewModel,
    options: DialogOption,
  ): void {
    this.deviceSettingBeforeEdit = options;

    // 各センサー設定は以下のように順番が異なるため、出力→表示の順番に変換する
    // 出力時：AlertMax, AlertMin, PreAlertMax, PreAlertMin
    // 表示時：AlertMax, PreAlertMax, PreAlertMin, AlertMin
    this.deviceSettingBeforeEdit.target = {
      deviceName:
        editedDeviceSetting.deviceName || editedDeviceSetting.deviceId,
      samplingFrequency: editedDeviceSetting.samplingFrequency,
      reportingFrequency: editedDeviceSetting.reportingFrequency,
      shipmentTracking: editedDeviceSetting.shipmentTracking,
      locationDetected: editedDeviceSetting.locationDetected,
      locationPriority: [
        editedDeviceSetting.locationPriority?.[0] || '',
        editedDeviceSetting.locationPriority?.[1] || '',
        editedDeviceSetting.locationPriority?.[2] || '',
      ],
      fallDetected: editedDeviceSetting.fallDetected,
      mcdDetected: editedDeviceSetting.mcdDetected,
      lightExposureDetected: editedDeviceSetting.lightExposureDetected,
      lightExposureDetectValue: editedDeviceSetting.lightExposureDetectValue,
      shockDetected: editedDeviceSetting.shockDetected,
      shockDetectedDetectValue: editedDeviceSetting.shockDetectedDetectValue,
      temperature: editedDeviceSetting.temperature,
      temperatureSettings: [
        editedDeviceSetting.temperatureSettings?.[0] || 0,
        editedDeviceSetting.temperatureSettings?.[2] || 0,
        editedDeviceSetting.temperatureSettings?.[3] || 0,
        editedDeviceSetting.temperatureSettings?.[1] || 0,
      ],
      pressure: editedDeviceSetting.pressure,
      pressureSettings: [
        editedDeviceSetting.pressureSettings?.[0] || 0,
        editedDeviceSetting.pressureSettings?.[2] || 0,
        editedDeviceSetting.pressureSettings?.[3] || 0,
        editedDeviceSetting.pressureSettings?.[1] || 0,
      ],
      humidity: editedDeviceSetting.humidity,
      humiditySettings: [
        editedDeviceSetting.humiditySettings?.[0] || 0,
        editedDeviceSetting.humiditySettings?.[2] || 0,
        editedDeviceSetting.humiditySettings?.[3] || 0,
        editedDeviceSetting.humiditySettings?.[1] || 0,
      ],
      tilt: editedDeviceSetting.tilt,
      tiltSettings: [
        editedDeviceSetting.tiltSettings?.[0] || 0,
        editedDeviceSetting.tiltSettings?.[2] || 0,
        editedDeviceSetting.tiltSettings?.[3] || 0,
        editedDeviceSetting.tiltSettings?.[1] || 0,
      ],
      light: editedDeviceSetting.light,
      lightSettings: [
        editedDeviceSetting.lightSettings?.[0] || 0,
        editedDeviceSetting.lightSettings?.[2] || 0,
        editedDeviceSetting.lightSettings?.[3] || 0,
        editedDeviceSetting.lightSettings?.[1] || 0,
      ],
      fmd: editedDeviceSetting.fmd,
    };
  }
}
